import type { QuestionBrowseTaxonomy } from '@course-authoring/shared-types';
import { QUESTION_BANK_BROWSE_COPY as COPY } from './questionBankBrowseCopy';
import type { QuestionBankBrowseStyles } from './questionBankBrowseStyles';

/** The sections' order (writeup); a taxonomy the surfaces do not know follows these. */
const SECTION_ORDER = ['Risk', 'Leadership', 'Culture'];

export const panelOrderOf = (name: string): number => {
  const index = SECTION_ORDER.indexOf(name);
  return index === -1 ? SECTION_ORDER.length : index;
};

/** The open-group key for one parent label in one taxonomy. */
export const groupKey = (taxonomyId: number, parentLabel: string): string => `${taxonomyId}:${parentLabel}`;

export interface QuestionBankTaxonomySectionsProps {
  styles: QuestionBankBrowseStyles;
  /** The scope's taxonomies from the facets; empty until they load. */
  taxonomies: readonly QuestionBrowseTaxonomy[];
  checkedLabelIds: readonly number[];
  openGroups: ReadonlySet<string>;
  /** Opens or closes these parent groups. */
  onSetGroups: (keys: readonly string[], open: boolean) => void;
  onToggleLabel: (labelId: number) => void;
}

/**
 * The framework sections shared by the page's taxonomy panel and the
 * picker's "Skills & risk areas" popover: Risk Areas, Leadership Skills and
 * Culture Skills, each a heading that opens or closes every parent, and
 * parents that open to child-label checkboxes with a count of the checked
 * ones. Owns no state.
 */
export function QuestionBankTaxonomySections({
  styles,
  taxonomies,
  checkedLabelIds,
  openGroups,
  onSetGroups,
  onToggleLabel,
}: QuestionBankTaxonomySectionsProps) {
  const b = styles.browse;
  const checked = new Set(checkedLabelIds);
  const ordered = [...taxonomies].sort((left, right) => panelOrderOf(left.name) - panelOrderOf(right.name) || left.taxonomyId - right.taxonomyId);

  return (
    <div style={b.panelSections}>
      {ordered.map((taxonomy) => {
        const keys = taxonomy.parents.map((parent) => groupKey(taxonomy.taxonomyId, parent.label));
        const allOpen = keys.length > 0 && keys.every((key) => openGroups.has(key));
        return (
          <section key={taxonomy.taxonomyId} style={b.panelSection}>
            <button
              type="button"
              aria-expanded={allOpen}
              style={b.panelHead}
              onClick={() => onSetGroups(keys, !allOpen)}
            >
              <span style={b.panelHeadName}>{taxonomy.displayName}</span>
              <span aria-hidden="true" style={{ ...b.panelChevron, ...(allOpen ? b.panelChevronOpen : {}) }} />
            </button>
            <ul style={b.panelParents}>
              {taxonomy.parents.map((parent) => {
                const key = groupKey(taxonomy.taxonomyId, parent.label);
                const open = openGroups.has(key);
                const checkedCount = parent.children.filter((child) => checked.has(child.labelId)).length;
                return (
                  <li key={key}>
                    <button type="button" aria-expanded={open} style={b.panelParentRow} onClick={() => onSetGroups([key], !open)}>
                      <span style={{ ...b.panelParentName, ...(open ? b.panelParentNameOpen : {}) }}>{parent.label}</span>
                      {checkedCount > 0 ? (
                        <span style={b.panelParentCount} title={COPY.checkedCount(checkedCount)}>{checkedCount}</span>
                      ) : null}
                      <span aria-hidden="true" style={{ ...b.panelChevron, ...(open ? b.panelChevronOpen : {}) }} />
                    </button>
                    {open ? (
                      <div role="group" aria-label={parent.label} style={b.panelChildren}>
                        {parent.children.map((child) => (
                          <label key={child.labelId} style={b.panelOption}>
                            <input
                              type="checkbox"
                              style={b.panelCheckbox}
                              checked={checked.has(child.labelId)}
                              onChange={() => onToggleLabel(child.labelId)}
                            />
                            <span>{child.label}</span>
                          </label>
                        ))}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
